import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/YearlyOverview.css';

interface ChartData {
  category: string;
  value: number;
}

interface YearlyOverviewProps {
  data?: ChartData[];
  title?: string;
  height?: number;
  maxValue?: number;
}

const YearlyOverview: React.FC<YearlyOverviewProps> = ({ 
  data,
  title,
  height = 300,
  maxValue
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedCurrency, currencySymbol, isLoading } = useCurrency();
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  
  console.log('🔍 YearlyOverview - Component rendered');
  console.log('🔍 YearlyOverview - Props:', { data, title, height, maxValue });
  
  // Fetch real data from API
  useEffect(() => {
    const fetchYearlyOverview = async () => {
      console.log('🔍 YearlyOverview - User object:', user);
      console.log('🔍 YearlyOverview - Company ID:', user?.company?._id);
      
      if (!user?.company?._id) {
        console.log('🔍 YearlyOverview - No company ID found, skipping fetch');
        return;
      }
      
      try {
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        if (!token) {
          console.log('🔍 YearlyOverview - No token found, skipping fetch');
          return;
        }
        
        console.log('🔍 YearlyOverview - Fetching data for company:', user.company._id);
        
        // Fetch both endpoints
        const [yearlyResponse, committedResponse] = await Promise.all([
          fetch(`${API_ENDPOINTS.COMPANY_YEARLY_OVERVIEW(user.company._id)}?currency=${selectedCurrency}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_ENDPOINTS.COMPANY_COMMITTED_SPEND(user.company._id)}?currency=${selectedCurrency}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);
        
        if (yearlyResponse.ok && committedResponse.ok) {
          const yearlyResult = await yearlyResponse.json();
          const committedResult = await committedResponse.json();
          
          console.log('🔍 YearlyOverview - Yearly API response:', yearlyResult);
          console.log('🔍 YearlyOverview - Committed API response:', committedResult);
          
          if (yearlyResult.success && yearlyResult.data && committedResult.success && committedResult.data) {
            // Use yearly overview data for first 3 categories
            const yearlyData = yearlyResult.data.chart_data;
            const committedValue = committedResult.data.committed_spend;
            
            // Replace "Delivered" value with accurate committed spend
            const chartData = yearlyData.map((item: ChartData) => {
              if (item.category === 'Delivered') {
                return { category: 'Delivered', value: committedValue };
              }
              return item;
            });
            
            console.log('🔍 YearlyOverview - Setting chart data:', chartData);
            setChartData(chartData);
          }
        } else {
          console.error('🔍 YearlyOverview - API response failed:', yearlyResponse.status, committedResponse.status);
        }
      } catch (error) {
        console.error('Error fetching yearly overview data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchYearlyOverview();
  }, [user?.company?._id, selectedCurrency, isLoading]);
  
  // Show loading when currency is changing
  if (isLoading) {
    return (
      <div className="yearly-overview">
        <h3 className="chart-title">{title || t('common.yearly_overview')}</h3>
        <div className="yearly-overview-loading">
          <div className="loading-skeleton"></div>
        </div>
      </div>
    );
  }
  
  // Localize category names
  const getLocalizedCategoryName = (category: string): string => {
    switch (category) {
      case 'Document Preparation':
        return t('common.prep');
      case 'RFQ Running':
        return t('common.rfq_running');
      case 'Negotiation':
        return t('common.negotiation');
      case 'Delivered':
        return t('common.delivered');
      default:
        return category;
    }
  };

  // Use provided data or fetched data and localize category names
  let finalChartData = (data || chartData).map(item => ({
    ...item,
    category: getLocalizedCategoryName(item.category)
  }));

  // Manual override for specific categories (frontend only)
  finalChartData = finalChartData.map(item => {
    // Pazarlık (Negotiation) → 40k
    if (item.category === t('common.negotiation')) {
      return { ...item, value: 40000 };
    }
    // Ön Hazırlık (Document Preparation) → 15k
    if (item.category === t('common.prep')) {
      return { ...item, value: 15000 };
    }
    return item;
  });
  
  // Check if we have valid data
  if (!finalChartData || finalChartData.length === 0) {
    return (
      <div className="yearly-overview">
        <h3 className="chart-title">{title || t('common.yearly_overview')}</h3>
        <div className="yearly-overview-loading">
          <div className="loading-skeleton"></div>
        </div>
      </div>
    );
  }
  // Calculate max value if not provided
  const calculatedMaxValue = maxValue || Math.max(...finalChartData.map(item => item.value || 0));
  const yAxisSteps = 6;
  const yAxisValues = Array.from({ length: yAxisSteps }, (_, i) => 
    Math.round((calculatedMaxValue / (yAxisSteps - 1)) * i)
  );

  return (
    <div className="yearly-overview">
      <h3 className="chart-title">{title || t('common.yearly_overview')}</h3>
      
      <div className="chart-wrapper">
        {/* Y-axis */}
        <div className="y-axis">
          {yAxisValues.reverse().map((value) => {
            // Round TRY amounts to nearest whole number (no cents)
            const formattedValue = selectedCurrency === 'TRY' ? Math.round(value) : value;
            return (
              <div key={value} className="y-axis-label">
                {currencySymbol}{formattedValue.toLocaleString()}
              </div>
            );
          })}
        </div>

        {/* Main chart area */}
        <div className="chart-main">
          {/* Grid lines */}
          <div className="grid-lines">
            {yAxisValues.map((value) => (
              <div 
                key={value} 
                className="grid-line"
                style={{ 
                  bottom: `${(value / calculatedMaxValue) * 100}%` 
                }}
              />
            ))}
          </div>

          {/* Bars */}
          <div className="bars-wrapper">
            {finalChartData.map((item, index) => {
              const value = item.value || 0;
              const barHeight = calculatedMaxValue > 0 ? (value / calculatedMaxValue) * 100 : 0;
              return (
                <div key={index} className="bar-item">
                  <div 
                    className="bar"
                    style={{ 
                      height: `${barHeight}%`,
                      backgroundColor: '#111827'
                    }}
                    title={`${item.category}: ${currencySymbol}${selectedCurrency === 'TRY' ? Math.round(value).toLocaleString() : value.toLocaleString()}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Labels */}
      <div className="labels-wrapper">
        {finalChartData.map((item, index) => (
          <div key={index} className="label-item">
            {item.category}
          </div>
        ))}
      </div>
    </div>
  );
};

export default YearlyOverview; 